/**
 * @lines98/client/components/settings-drawer — settings-панель: выезжающий снизу
 * список + drill-down под-экран «Оформление доски» (§5.5, owner-decision 2026-06-04).
 *
 * МОДЕЛЬ (после отказа owner'а от прежней версии — причины: «выезжала справа» и
 * «во время партии просила бросить игру»):
 *   - Открывается из ШЕСТЕРЁНКИ внизу рейла (утилита, не пункт-направление). НЕ
 *     проходит через leave-guard → во время партии НИЧЕГО не спрашивает, фаза не
 *     меняется, партия не сбрасывается (§6.12 / AC-17). Открывается одинаково
 *     отовсюду (игра / доска дня / меню).
 *   - **Выезжает СНИЗУ** (bottom-sheet) на всех viewport-классах — не «справа».
 *   - **Drill-down:** трек из двух страниц — [список настроек] | [галерея скинов];
 *     «Оформление доски» уезжает на галерею (стрелка «назад» возвращает). Всё —
 *     оверлей поверх доски (модель «Настройки айфона → Обои»).
 *
 * Контент списка = ОБЩИЙ `lib/settings-list` (анимация / тема / дальтоники), single
 * source. Галерея = `components/skin-gallery` (из реестра `lib/skin-registry`).
 *
 * MECHANICS: ТА ЖЕ механика, что у бокового nav-rail (owner-playtest 2026-06-04
 * «должен выезжать плавно снизу, как боковой рейл»): постоянный элемент (НЕ модальный
 * `<dialog>`/showModal — тот давал top-layer + UA-центрирование + scroll-lock → «прыжок
 * сверху» и мелькание скроллбара на ПК) + scrim + transform-park `translateY(110%)` +
 * класс `--open` едет в 0; всё через `transition: var(--t)` (GPU-only). Esc + клик по
 * scrim закрывают (раньше из коробки давал модальный dialog). `prefers-reduced-motion`
 * гасит. SINGLETON: один на app-shell; `open()` сбрасывает на список.
 *
 * Spec: extra-modes §5.5 + §6.12; SKILL §0.E (drawer-семья) + §0.G (токены) + §9.
 */

import './settings-drawer.css';

import { buildSettingRows } from '../lib/settings-list.js';
import { t } from '../lib/i18n/index.js';
import { useGameStore } from '../store/game-store.js';
import { createIconButton, ICON_CLOSE } from './icon-button.js';
import { type SettingRowHandle } from './setting-row.js';
import { mountSkinGallery, type SkinGalleryHandle } from './skin-gallery.js';

// «Оформление» / «Назад к настройкам» — i18n (game.settingsScreen.appearance /
// backToSettings). Резолвятся `t()` per-mount (locale-реактивность).
// NB: drill-row «Оформление» временно убран (owner 2026-06-25) — ICON_CHEVRON /
// panel-row / text больше не нужны; вернутся вместе со строкой при откате.

const ICON_BACK = `
  <svg class="settings-back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M15 6l-6 6 6 6" /></svg>`;

export interface SettingsDrawerHandle {
  /** Открыть панель (на странице списка). `trigger` игнорируется (bottom-sheet). */
  open(trigger?: HTMLElement): void;
  /**
   * Открыть панель СРАЗУ на под-экране «Оформление» (drill-down). Используется
   * demo-маршрутом `?demo=appearance` для viewport-baseline под-экрана.
   */
  openAppearance(): void;
  /** Закрыть панель (как tap-вне / Esc / ×). */
  close(): void;
  /** Teardown: закрыть + снять DOM/слушатели. */
  unmount(): void;
}

export function mountSettingsDrawer(): SettingsDrawerHandle {
  // Оверлей-корень — обычный div (НЕ <dialog>), всегда в DOM, transform-park.
  const root = document.createElement('div');
  root.className = 'settings-drawer';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', t('game.settingsScreen.title'));

  // Затемнитель (как .nav-scrim): клик по нему закрывает.
  const scrim = document.createElement('div');
  scrim.className = 'settings-scrim';

  const panel = document.createElement('div');
  panel.className = 'settings-drawer-panel';

  // Трек drill-down: [страница-список] | [страница-галерея], ширина 200%, едет влево.
  const track = document.createElement('div');
  track.className = 'settings-drawer-track';

  // ── Страница 1: список настроек ──────────────────────────────────────────────
  const listPage = document.createElement('section');
  listPage.className = 'settings-drawer-page';

  const listHeader = document.createElement('header');
  listHeader.className = 'settings-drawer-header';
  const listTitle = document.createElement('h2');
  listTitle.className = 'settings-drawer-title';
  listTitle.textContent = t('game.settingsScreen.title');
  // × — примитив icon-button (ghost, тактильные 44px, SVG-крестик по центру).
  const closeBtn = createIconButton({
    glyph: ICON_CLOSE,
    label: t('game.settingsScreen.drawerClose'),
    className: 'settings-drawer-close',
  }).element;
  listHeader.append(listTitle, closeBtn);

  const list = document.createElement('div');
  list.className = 'settings-drawer-list';
  list.setAttribute('role', 'list');
  list.setAttribute('aria-label', t('game.settingsScreen.listAria'));

  // Общие строки (single source). `let` — пересобираются на смену языка (relabel
  // ниже): drawer — singleton app-shell, НЕ проходит через ре-mount фазы, поэтому
  // подписан на locale сам (тот же приём, что nav-rail applyRailLocale).
  let rows: SettingRowHandle[] = buildSettingRows();
  for (const r of rows) {
    r.element.setAttribute('role', 'listitem');
    list.appendChild(r.element);
  }

  // ВРЕМЕННО УБРАНО (owner-directive 2026-06-25): drill-row «Оформление доски» —
  // точка входа из «Настроек» на под-экран-галерею. Owner вернётся к оформлению доски/
  // шаров позже; чтобы откат был тривиальным, вход снят отдельным коммитом. Сам
  // под-экран-галерея (страница 2 ниже), `showGallery()` / `openAppearance()` и
  // demo-маршрут `?demo=appearance` ОСТАЮТСЯ рабочими — убрана ТОЛЬКО видимая строка
  // в списке настроек.

  listPage.append(listHeader, list);

  // ── Страница 2: галерея скинов (монтируется лениво) ──────────────────────────
  const galleryPage = document.createElement('section');
  galleryPage.className = 'settings-drawer-page';

  const galleryHeader = document.createElement('header');
  galleryHeader.className = 'settings-drawer-header';
  const backBtn = document.createElement('button');
  backBtn.type = 'button';
  backBtn.className = 'settings-drawer-back';
  // aria «Назад к настройкам» → i18n. Видимая подпись «Назад»
  // (.settings-drawer-back-label) — game.settingsScreen.back.
  backBtn.setAttribute('aria-label', t('game.settingsScreen.backToSettings'));
  const backLabel = document.createElement('span');
  backLabel.className = 'settings-drawer-back-label';
  backLabel.textContent = t('game.settingsScreen.back');
  backBtn.innerHTML = ICON_BACK;
  backBtn.appendChild(backLabel);
  const galleryTitle = document.createElement('h2');
  galleryTitle.className = 'settings-drawer-title';
  galleryTitle.textContent = t('game.settingsScreen.appearance');
  galleryHeader.append(backBtn, galleryTitle);

  const galleryBody = document.createElement('div');
  galleryBody.className = 'settings-drawer-gallery-body';

  galleryPage.append(galleryHeader, galleryBody);

  track.append(listPage, galleryPage);
  panel.appendChild(track);
  root.append(scrim, panel);
  document.body.appendChild(root);

  // Локализация singleton-drawer'а: он живёт весь lifetime app-shell и НЕ ре-монтируется
  // на смену языка (в отличие от per-phase экранов) → подписан на locale сам, как nav-rail
  // (applyRailLocale). relabel() перерезолвит статические подписи и ПЕРЕСОБЕРЁТ строки
  // настроек (buildSettingRows читает свежие prefs + активный t()) на новом языке.
  const relabel = (): void => {
    root.setAttribute('aria-label', t('game.settingsScreen.title'));
    listTitle.textContent = t('game.settingsScreen.title');
    closeBtn.setAttribute('aria-label', t('game.settingsScreen.drawerClose'));
    list.setAttribute('aria-label', t('game.settingsScreen.listAria'));
    backLabel.textContent = t('game.settingsScreen.back');
    // Пересобрать строки настроек на новом языке (старые — destroy, чтоб снять их
    // `?`-поповеры). Drill-row «Оформление» временно убран → строки просто аппендятся.
    for (const r of rows) {
      r.destroy();
      r.element.remove();
    }
    rows = buildSettingRows();
    for (const r of rows) {
      r.element.setAttribute('role', 'listitem');
      list.appendChild(r.element);
    }
  };
  let prevLocale = useGameStore.getState().locale;
  const unsubLocale = useGameStore.subscribe((s) => {
    if (s.locale === prevLocale) return;
    prevLocale = s.locale;
    relabel();
    // Под-экран «Оформление» (galery) — singleton как и drawer (lazy, но не
    // ре-монтируется): его вкладки/aria/имена свотчей запечены `t()`-строками при
    // сборке → пере-собрать тело на новом языке (выбор осей persist, не теряется).
    gallery?.relabel();
  });

  let unmounted = false;
  let gallery: SkinGalleryHandle | null = null;

  const showGallery = (): void => {
    if (!gallery) {
      gallery = mountSkinGallery();
      galleryBody.appendChild(gallery.element);
    }
    root.classList.add('settings-drawer--gallery');
  };
  const showList = (): void => {
    root.classList.remove('settings-drawer--gallery');
  };

  let isOpen = false;
  let lastFocused: HTMLElement | null = null;

  const open = (): void => {
    if (unmounted || isOpen) return;
    lastFocused = (document.activeElement as HTMLElement | null) ?? null;
    showList(); // всегда стартуем со списка
    isOpen = true;
    root.classList.add('settings-drawer--open'); // CSS: панель едет снизу + scrim
    closeBtn.focus({ preventScroll: true });
  };

  const openAppearance = (): void => {
    if (unmounted) return;
    if (!isOpen) {
      lastFocused = (document.activeElement as HTMLElement | null) ?? null;
      isOpen = true;
      root.classList.add('settings-drawer--open');
    }
    showGallery(); // drill-down сразу на под-экран «Оформление»
  };

  const close = (): void => {
    if (unmounted || !isOpen) return;
    isOpen = false;
    root.classList.remove('settings-drawer--open'); // CSS: панель уезжает вниз + scrim гаснет
    lastFocused?.focus?.({ preventScroll: true });
  };

  const onBack = (e: Event): void => {
    e.preventDefault();
    showList();
  };
  const onCloseClick = (e: Event): void => {
    e.stopPropagation();
    e.preventDefault();
    close();
  };
  // Клик по затемнителю (вне панели) закрывает.
  const onScrimClick = (): void => close();
  // Esc закрывает (раньше из коробки давал модальный <dialog>).
  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      close();
    }
  };

  backBtn.addEventListener('click', onBack);
  closeBtn.addEventListener('click', onCloseClick);
  scrim.addEventListener('click', onScrimClick);
  document.addEventListener('keydown', onKeydown);

  return {
    open,
    openAppearance,
    close,
    unmount(): void {
      if (unmounted) return;
      unmounted = true;
      isOpen = false;
      unsubLocale();
      backBtn.removeEventListener('click', onBack);
      closeBtn.removeEventListener('click', onCloseClick);
      scrim.removeEventListener('click', onScrimClick);
      document.removeEventListener('keydown', onKeydown);
      for (const r of rows) r.destroy();
      gallery?.destroy();
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}
